import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";

const C = {
  accent: "#345479", green: "#2d6a4f", red: "#9b2226", border: "#D9D3CC",
  cream: "#F5F2EF", beige: "#ECE7E2", charcoal: "#333332",
};

const s = {
  card: { background: "#fff", border: "1px solid #D9D3CC", borderRadius: 6, padding: 16, marginBottom: 12 },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 },
  btn: { padding: "6px 14px", borderRadius: 4, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  input: { padding: "6px 8px", border: "1px solid #D9D3CC", borderRadius: 4, fontSize: 12, width: "100%" },
  select: { padding: "6px 8px", border: "1px solid #D9D3CC", borderRadius: 4, fontSize: 12, width: "100%" },
  sectionHdr: { fontSize: 13, fontWeight: 700, color: "#333332", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" },
};

const fmt = (v) => v != null ? "$" + Number(v).toLocaleString("en-US", { minimumFractionDigits: 0 }) : "\u2014";

const PRICE_TYPES = [
  { value: "fixed", label: "Fixed Price" },
  { value: "variable", label: "Variable (VWAP)" },
  { value: "floor", label: "Floor Price" },
  { value: "ceiling", label: "Ceiling Price" },
  { value: "alternative", label: "Alternative" },
  { value: "mandatory", label: "Mandatory" },
  { value: "optional", label: "Optional" },
  { value: "make_whole", label: "Make-Whole" },
];

const COST_METHODS = [
  { value: "manual", label: "Manual" },
  { value: "zero", label: "Zero Basis (Bonus)" },
  { value: "relative_fv", label: "Relative Face Value" },
];

function emptySecForm() {
  return { security: "", series_label: "", face_value: "", cost_basis: "", cost_basis_method: "manual", is_primary: false, is_bonus: false, notes: "" };
}

function emptyTermForm() {
  return { price_type: "fixed", price_value: "", formula: "", discount_pct: "", lookback_days: "", floor_price: "", ceiling_price: "", conversion_ratio: "", condition_description: "", is_default: false, is_active: true, effective_date: "", expiry_date: "", notes: "" };
}

export default function DealSecuritiesPanel({ dealId }) {
  const [securities, setSecurities] = useState([]);
  const [allSecDefs, setAllSecDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddSec, setShowAddSec] = useState(false);
  const [editingSecId, setEditingSecId] = useState(null);
  const [secForm, setSecForm] = useState(emptySecForm());
  const [saving, setSaving] = useState(false);
  const [createNew, setCreateNew] = useState(false);
  const [newSec, setNewSec] = useState({ ticker: "", name: "", instrument_type: "Preferred" });
  const [error, setError] = useState("");
  const [expandedSec, setExpandedSec] = useState(null);
  const [showAddTerm, setShowAddTerm] = useState(null);
  const [editingTermId, setEditingTermId] = useState(null);
  const [termForm, setTermForm] = useState(emptyTermForm());

  const loadSecurities = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const r = await api.get("/securities/deal-securities/", { params: { deal: dealId } });
      setSecurities(r.data.results || r.data || []);
    } catch { setSecurities([]); }
    setLoading(false);
  }, [dealId]);

  useEffect(() => { loadSecurities(); }, [loadSecurities]);
  useEffect(() => {
    api.get("/securities/securities/", { params: { page_size: 500 } })
      .then(r => setAllSecDefs(r.data.results || r.data || []))
      .catch(() => {});
  }, []);

  const handleSaveSec = async () => {
    setSaving(true); setError("");
    try {
      let secId = parseInt(secForm.security);
      if (createNew && newSec.ticker && newSec.name) {
        const secResp = await api.post("/securities/securities/", {
          ticker: newSec.ticker, name: newSec.name, instrument_type: newSec.instrument_type,
          issuer: "", cusip: "", isin: "",
        });
        secId = secResp.data.id;
        setAllSecDefs(prev => [...prev, secResp.data]);
        setCreateNew(false);
        setNewSec({ ticker: "", name: "", instrument_type: "Preferred" });
      }
      const payload = {
        deal: dealId, security: secId, series_label: secForm.series_label,
        face_value: parseFloat(secForm.face_value) || 0,
        cost_basis: secForm.is_bonus ? 0 : (parseFloat(secForm.cost_basis) || 0),
        cost_basis_method: secForm.is_bonus ? "zero" : secForm.cost_basis_method,
        is_primary: secForm.is_primary, is_bonus: secForm.is_bonus, notes: secForm.notes,
      };
      if (editingSecId) await api.put("/securities/deal-securities/" + editingSecId + "/", payload);
      else await api.post("/securities/deal-securities/", payload);
      setShowAddSec(false); setEditingSecId(null); setSecForm(emptySecForm());
      await loadSecurities();
    } catch (e) { setError(e.response?.data ? JSON.stringify(e.response.data) : "Save failed"); }
    setSaving(false);
  };

  const handleDeleteSec = async (id) => {
    if (!window.confirm("Delete this security from the deal?")) return;
    try { await api.delete("/securities/deal-securities/" + id + "/"); await loadSecurities(); }
    catch (e) { setError(e.response?.data?.detail || "Delete failed"); }
  };

  const startEditSec = (ds) => {
    setSecForm({ security: ds.security, series_label: ds.series_label || "", face_value: ds.face_value || "", cost_basis: ds.cost_basis || "", cost_basis_method: ds.cost_basis_method || "manual", is_primary: ds.is_primary, is_bonus: ds.is_bonus, notes: ds.notes || "" });
    setEditingSecId(ds.id); setShowAddSec(true);
  };

  const handleSaveTerm = async (dsId) => {
    setSaving(true); setError("");
    try {
      const p = {
        deal_security: dsId, deal: dealId, price_type: termForm.price_type,
        price_value: termForm.price_value ? parseFloat(termForm.price_value) : null,
        formula: termForm.formula, discount_pct: termForm.discount_pct ? parseFloat(termForm.discount_pct) : null,
        lookback_days: termForm.lookback_days ? parseInt(termForm.lookback_days) : null,
        floor_price: termForm.floor_price ? parseFloat(termForm.floor_price) : null,
        ceiling_price: termForm.ceiling_price ? parseFloat(termForm.ceiling_price) : null,
        conversion_ratio: termForm.conversion_ratio ? parseFloat(termForm.conversion_ratio) : null,
        condition_description: termForm.condition_description, is_default: termForm.is_default,
        is_active: termForm.is_active, effective_date: termForm.effective_date || null,
        expiry_date: termForm.expiry_date || null, notes: termForm.notes,
      };
      if (editingTermId) await api.put("/securities/conversion-terms/" + editingTermId + "/", p);
      else await api.post("/securities/conversion-terms/", p);
      setShowAddTerm(null); setEditingTermId(null); setTermForm(emptyTermForm());
      await loadSecurities();
    } catch (e) { setError(e.response?.data ? JSON.stringify(e.response.data) : "Save failed"); }
    setSaving(false);
  };

  const handleDeleteTerm = async (id) => {
    if (!window.confirm("Delete this conversion term?")) return;
    try { await api.delete("/securities/conversion-terms/" + id + "/"); await loadSecurities(); }
    catch (e) { setError(e.response?.data?.detail || "Delete failed"); }
  };

  const startEditTerm = (term, dsId) => {
    setTermForm({ price_type: term.price_type || "fixed", price_value: term.price_value || "", formula: term.formula || "", discount_pct: term.discount_pct || "", lookback_days: term.lookback_days || "", floor_price: term.floor_price || "", ceiling_price: term.ceiling_price || "", conversion_ratio: term.conversion_ratio || "", condition_description: term.condition_description || "", is_default: term.is_default, is_active: term.is_active, effective_date: term.effective_date || "", expiry_date: term.expiry_date || "", notes: term.notes || "" });
    setEditingTermId(term.id); setShowAddTerm(dsId);
  };

  if (!dealId) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={s.sectionHdr}>
        <span>Securities ({securities.length})</span>
        <button style={{ ...s.btn, background: C.accent, color: "#fff" }}
          onClick={() => { setShowAddSec(true); setEditingSecId(null); setSecForm(emptySecForm()); }}>+ Add Security</button>
      </div>
      {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {loading ? <div style={{ color: "#999", fontSize: 12 }}>Loading...</div> : securities.map(ds => (
        <div key={ds.id} style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{ds.security_ticker || "Security #" + ds.security}</span>
              {ds.series_label && <span style={{ color: "#666", fontSize: 12 }}>{"\u2014"} {ds.series_label}</span>}
              {ds.is_primary && <span style={{ ...s.badge, background: C.accent, color: "#fff" }}>PRIMARY</span>}
              {ds.is_bonus && <span style={{ ...s.badge, background: C.green, color: "#fff" }}>BONUS</span>}
              <span style={{ ...s.badge, background: C.cream, color: C.charcoal }}>{ds.instrument_type || ""}</span>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button style={{ ...s.btn, background: C.accent, color: "#fff" }} onClick={() => startEditSec(ds)}>Edit</button>
              <button style={{ ...s.btn, background: expandedSec === ds.id ? C.charcoal : C.cream, color: expandedSec === ds.id ? "#fff" : C.charcoal }}
                onClick={() => setExpandedSec(expandedSec === ds.id ? null : ds.id)}>
                Terms ({(ds.conversion_terms || []).length})
              </button>
              {!ds.is_primary && <button style={{ ...s.btn, background: C.red, color: "#fff" }} onClick={() => handleDeleteSec(ds.id)}>{"\u00D7"}</button>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, fontSize: 12 }}>
            <div><span style={{ color: "#888" }}>Face Value:</span> <strong>{fmt(ds.face_value)}</strong></div>
            <div><span style={{ color: "#888" }}>Cost Basis:</span> <strong>{fmt(ds.cost_basis)}</strong></div>
            <div><span style={{ color: "#888" }}>Method:</span> {ds.cost_basis_method}</div>
            {ds.security_name && <div><span style={{ color: "#888" }}>Issuer:</span> {ds.security_name}</div>}
          </div>
          {expandedSec === ds.id && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #D9D3CC" }}>
              <div style={{ ...s.sectionHdr, fontSize: 12 }}>
                <span>Conversion Terms</span>
                <button style={{ ...s.btn, background: C.green, color: "#fff", fontSize: 11 }}
                  onClick={() => { setShowAddTerm(ds.id); setEditingTermId(null); setTermForm(emptyTermForm()); }}>+ Add Term</button>
              </div>
              {(ds.conversion_terms || []).length === 0 && <div style={{ color: "#999", fontSize: 12, fontStyle: "italic" }}>No conversion terms defined.</div>}
              {(ds.conversion_terms || []).map(term => (
                <div key={term.id} style={{ background: C.cream, borderRadius: 4, padding: 10, marginBottom: 6, fontSize: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ ...s.badge, background: C.accent, color: "#fff" }}>
                        {PRICE_TYPES.find(p => p.value === term.price_type)?.label || term.price_type}
                      </span>
                      {term.price_value && <strong>{"$" + Number(term.price_value).toFixed(4)}</strong>}
                      {term.formula && <span style={{ color: "#666" }}>{term.formula}</span>}
                      {term.is_default && <span style={{ ...s.badge, background: "#f0ad4e", color: "#fff" }}>DEFAULT</span>}
                      {!term.is_active && <span style={{ ...s.badge, background: "#999", color: "#fff" }}>INACTIVE</span>}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button style={{ ...s.btn, background: C.accent, color: "#fff", fontSize: 10 }} onClick={() => startEditTerm(term, ds.id)}>Edit</button>
                      <button style={{ ...s.btn, background: C.red, color: "#fff", fontSize: 10 }} onClick={() => handleDeleteTerm(term.id)}>{"\u00D7"}</button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, marginTop: 4, flexWrap: "wrap" }}>
                    {term.discount_pct && <span><span style={{ color: "#888" }}>Discount:</span> {term.discount_pct}%</span>}
                    {term.lookback_days && <span><span style={{ color: "#888" }}>Lookback:</span> {term.lookback_days} days</span>}
                    {term.floor_price && <span><span style={{ color: "#888" }}>Floor:</span> {"$" + Number(term.floor_price).toFixed(4)}</span>}
                    {term.ceiling_price && <span><span style={{ color: "#888" }}>Ceiling:</span> {"$" + Number(term.ceiling_price).toFixed(4)}</span>}
                    {term.conversion_ratio && <span><span style={{ color: "#888" }}>Ratio:</span> {Number(term.conversion_ratio).toFixed(4)}</span>}
                    {term.effective_date && <span><span style={{ color: "#888" }}>From:</span> {term.effective_date}</span>}
                    {term.expiry_date && <span><span style={{ color: "#888" }}>To:</span> {term.expiry_date}</span>}
                  </div>
                  {term.condition_description && <div style={{ marginTop: 4, color: "#666", fontStyle: "italic" }}>{term.condition_description}</div>}
                </div>
              ))}
              {showAddTerm === ds.id && renderTermForm(ds.id)}
            </div>
          )}
        </div>
      ))}
      {showAddSec && renderSecForm()}
    </div>
  );

  function renderSecForm() {
    return (
      <div style={{ ...s.card, background: C.beige }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{editingSecId ? "Edit Security" : "Add Security to Deal"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Security</label>
            <select style={s.select} value={secForm.security} onChange={e => { const v = e.target.value; if (v === "__new__") { setCreateNew(true); setSecForm({ ...secForm, security: "" }); } else { setCreateNew(false); setSecForm({ ...secForm, security: v }); } }}>
              <option value="">{"\u2014"} Select Security {"\u2014"}</option>
              {allSecDefs.map(sd => <option key={sd.id} value={sd.id}>{sd.ticker} {"\u2014"} {sd.name} ({sd.instrument_type})</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Series Label</label>
            <input style={s.input} value={secForm.series_label} placeholder="e.g. Series A Preferred" onChange={e => setSecForm({ ...secForm, series_label: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Face Value ($)</label>
            <input style={s.input} type="number" value={secForm.face_value} onChange={e => setSecForm({ ...secForm, face_value: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Cost Basis ($)</label>
            <input style={s.input} type="number" value={secForm.is_bonus ? "0" : secForm.cost_basis} disabled={secForm.is_bonus} onChange={e => setSecForm({ ...secForm, cost_basis: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Cost Method</label>
            <select style={s.select} value={secForm.is_bonus ? "zero" : secForm.cost_basis_method} disabled={secForm.is_bonus} onChange={e => setSecForm({ ...secForm, cost_basis_method: e.target.value })}>
              {COST_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "center", paddingTop: 18 }}>
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={secForm.is_primary} onChange={e => setSecForm({ ...secForm, is_primary: e.target.checked })} /> Primary
            </label>
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={secForm.is_bonus} onChange={e => setSecForm({ ...secForm, is_bonus: e.target.checked, cost_basis: e.target.checked ? "0" : secForm.cost_basis, cost_basis_method: e.target.checked ? "zero" : "manual" })} /> Bonus (no cost)
            </label>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: 11, color: "#888" }}>Notes</label>
          <input style={s.input} value={secForm.notes} onChange={e => setSecForm({ ...secForm, notes: e.target.value })} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button style={{ ...s.btn, background: C.green, color: "#fff" }} onClick={handleSaveSec} disabled={saving}>{saving ? "Saving..." : (editingSecId ? "Update" : "Add Security")}</button>
          <button style={{ ...s.btn, background: "#999", color: "#fff" }} onClick={() => { setShowAddSec(false); setEditingSecId(null); setCreateNew(false); }}>Cancel</button>
        </div>
      </div>
    );
  }

  function renderTermForm(dsId) {
    const isVar = termForm.price_type === "variable";
    const hasPrice = ["fixed", "floor", "ceiling", "alternative", "mandatory", "optional", "make_whole"].includes(termForm.price_type);
    return (
      <div style={{ ...s.card, background: C.beige, marginTop: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{editingTermId ? "Edit Conversion Term" : "Add Conversion Term"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Price Type</label>
            <select style={s.select} value={termForm.price_type} onChange={e => setTermForm({ ...termForm, price_type: e.target.value })}>
              {PRICE_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {hasPrice && <div>
            <label style={{ fontSize: 11, color: "#888" }}>Price ($)</label>
            <input style={s.input} type="number" step="0.0001" value={termForm.price_value} onChange={e => setTermForm({ ...termForm, price_value: e.target.value })} />
          </div>}
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Conversion Ratio</label>
            <input style={s.input} type="number" step="0.00000001" value={termForm.conversion_ratio} placeholder="shares per $1 face" onChange={e => setTermForm({ ...termForm, conversion_ratio: e.target.value })} />
          </div>
        </div>
        {isVar && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Formula</label>
            <input style={s.input} value={termForm.formula} placeholder="e.g. 90% of 5-day VWAP" onChange={e => setTermForm({ ...termForm, formula: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Discount %</label>
            <input style={s.input} type="number" step="0.01" value={termForm.discount_pct} onChange={e => setTermForm({ ...termForm, discount_pct: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Lookback Days</label>
            <input style={s.input} type="number" value={termForm.lookback_days} onChange={e => setTermForm({ ...termForm, lookback_days: e.target.value })} />
          </div>
        </div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Floor Price</label>
            <input style={s.input} type="number" step="0.0001" value={termForm.floor_price} onChange={e => setTermForm({ ...termForm, floor_price: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Ceiling Price</label>
            <input style={s.input} type="number" step="0.0001" value={termForm.ceiling_price} onChange={e => setTermForm({ ...termForm, ceiling_price: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Effective Date</label>
            <input style={s.input} type="date" value={termForm.effective_date} onChange={e => setTermForm({ ...termForm, effective_date: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Expiry Date</label>
            <input style={s.input} type="date" value={termForm.expiry_date} onChange={e => setTermForm({ ...termForm, expiry_date: e.target.value })} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, marginTop: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: "#888" }}>Condition</label>
            <input style={s.input} value={termForm.condition_description} placeholder="When is this conversion available?" onChange={e => setTermForm({ ...termForm, condition_description: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", paddingTop: 16 }}>
            <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={termForm.is_default} onChange={e => setTermForm({ ...termForm, is_default: e.target.checked })} /> Default
            </label>
            <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={termForm.is_active} onChange={e => setTermForm({ ...termForm, is_active: e.target.checked })} /> Active
            </label>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button style={{ ...s.btn, background: C.green, color: "#fff" }} onClick={() => handleSaveTerm(dsId)} disabled={saving}>{saving ? "Saving..." : (editingTermId ? "Update" : "Add Term")}</button>
          <button style={{ ...s.btn, background: "#999", color: "#fff" }} onClick={() => { setShowAddTerm(null); setEditingTermId(null); }}>Cancel</button>
        </div>
      </div>
    );
  }
}
